import { useMemo } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import { Icon } from '@antimatterhq/ui/components/icon';
import { Text } from '@antimatterhq/ui/components/typography';
import { DatasourceUpgradeFieldSpecDiff } from '@/lib/datasource/upgrade/datasourceUpgrade.types';
import { getDefinition } from '@/lib/datasource';
import { cn } from '@/lib/utils';

const FieldSpecItem = ({
  name,
  definition,
  strikeThrough = false,
  testidPrefix,
}: {
  name: string;
  definition: string;
  strikeThrough?: boolean;
  testidPrefix: string;
}) => (
  <div className="text-left">
    <div>
      <Text>Name:</Text>&nbsp;
      <Text
        bold
        className={cn(strikeThrough && 'line-through')}
        data-testid={`${testidPrefix}-name`}
      >
        {name}
      </Text>
    </div>
    <div className="max-h-[200px] overflow-y-auto custom-scrollbar">
      <Text>Definition:</Text>&nbsp;
      <Text
        bold
        className={cn(strikeThrough && 'line-through')}
        data-testid={`${testidPrefix}-definition`}
      >
        {definition}
      </Text>
    </div>
  </div>
);

export const DatasourceUpgradeDiffFieldSpec = ({
  diff,
  onAccept,
  onReject,
  isAccepting,
  isRejecting,
}: {
  diff: DatasourceUpgradeFieldSpecDiff;
  onAccept: () => void;
  onReject: () => void;
  isAccepting?: boolean;
  isRejecting?: boolean;
}) => {
  const action = useMemo(() => {
    if (!diff.upgradePresetValue && diff.currentPresetValue) {
      return 'delete';
    }
    if (diff.upgradePresetValue && !diff.currentPresetValue) {
      return 'add';
    }
    return 'update';
  }, [diff.currentPresetValue, diff.upgradePresetValue]);

  const accepted = useMemo(() => {
    if (diff.status !== 'confirmed') {
      return false;
    }
    // if added then accepted means that there shouldn't be an omitted field in the upgrade
    if (action === 'add') {
      return !diff.upgradeOmitted;
    }

    return !diff.upgradeValue;
  }, [action, diff.upgradeValue, diff.status, diff.upgradeOmitted]);

  const rejected = useMemo(() => {
    if (diff.status !== 'confirmed') {
      return false;
    }
    // if add is rejected then there should be an omitted field in the upgrade
    if (action === 'add') {
      return diff.upgradeOmitted;
    }

    return !!diff.upgradeValue;
  }, [action, diff.upgradeValue, diff.status, diff.upgradeOmitted]);

  return (
    <div
      className="flex w-full flex-col"
      data-testid={`fieldspec-diff-root-${diff.fieldName}`}
    >
      <div className="flex w-full gap-4">
        <div
          className="flex-1 text-center"
          data-testid="fieldspec-diff-current-container"
        >
          <div className="flex items-center">
            <div className="flex-1"></div>
            {diff.currentPresetValue && (
              <FieldSpecItem
                name={diff.currentPresetValue.name ?? ''}
                definition={getDefinition(diff.currentPresetValue)}
                testidPrefix="fieldspec-diff-current"
              />
            )}

            <div className="flex-1">
              {rejected && (
                <Icon
                  icon="lucide:check"
                  className="ml-2 text-green-500"
                  data-testid="fieldspec-diff-rejected-icon"
                />
              )}
            </div>
          </div>
        </div>
        <div
          className="flex-1 text-center"
          data-testid="fieldspec-diff-upgrade-container"
        >
          <div className="flex items-center">
            <div className="flex-1"></div>
            {diff.upgradePresetValue && (
              <FieldSpecItem
                name={diff.upgradePresetValue.name ?? ''}
                definition={getDefinition(diff.upgradePresetValue)}
                testidPrefix="fieldspec-diff-upgrade"
              />
            )}
            {action === 'delete' && (
              <FieldSpecItem
                name={diff.currentPresetValue?.name ?? ''}
                definition={getDefinition(diff.currentPresetValue!)}
                strikeThrough
                testidPrefix="fieldspec-diff-current"
              />
            )}
            <div className="flex-1">
              {accepted && (
                <Icon
                  icon="lucide:check"
                  className="ml-2 text-green-500"
                  data-testid="fieldspec-diff-accepted-icon"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full pt-4">
        <div className="flex-1 text-center">
          <Button
            variant="default"
            onClick={onReject}
            disabled={rejected || isRejecting || isAccepting}
            data-testid="datasource-upgrade-diff-fieldspec-reject-btn"
          >
            {action === 'delete' && 'Keep existing field'}
            {action === 'add' && 'Ignore new field'}
            {action === 'update' && 'Keep existing field'}
          </Button>
        </div>
        <div className="flex-1 text-center">
          <Button
            variant="primary"
            onClick={onAccept}
            disabled={accepted || isRejecting || isAccepting}
            data-testid="datasource-upgrade-diff-fieldspec-accept-btn"
          >
            {action === 'delete' && 'Remove field'}
            {action === 'add' && 'Add field'}
            {action === 'update' && 'Update field'}
          </Button>
        </div>
      </div>
    </div>
  );
};
